/**
 * Platform admin card: list the latest grading workflow runs
 * (GET /api/admin/workflow-runs) and look one up by UUID to list its node runs
 * (GET /api/admin/workflow-runs/{id}). Metadata only; debug payloads are never
 * requested.
 */
import React, { useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { showDialog } from '../../utils/dialogs';

const RECENT_RUNS_LIMIT = 10;

interface WorkflowRunSummary {
  id: string;
  workflow_type: string;
  status: string;
  user_id?: number | null;
  created_at: string;
}

export default function WorkflowRunInspectorCard() {
  const { t } = useTranslation();
  const [runIdInput, setRunIdInput] = useState('');
  const [runData, setRunData] = useState<any>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [recentRuns, setRecentRuns] = useState<WorkflowRunSummary[] | null>(null);
  const [recentLoading, setRecentLoading] = useState(false);

  const inspectRun = async (runId: string) => {
    if (!runId.trim()) return;
    setRunLoading(true);
    try {
      const data = await apiRequest({ endpoint: `/admin/workflow-runs/${runId.trim()}` });
      setRunData(data);
    } catch (e: unknown) {
      showDialog(t('platformAdmin.workflowRuns.errorTitle'), (e as Error).message);
      setRunData(null);
    } finally {
      setRunLoading(false);
    }
  };

  const loadRecentRuns = async () => {
    setRecentLoading(true);
    try {
      const data = await apiRequest<WorkflowRunSummary[]>({
        endpoint: '/admin/workflow-runs',
        params: { limit: RECENT_RUNS_LIMIT },
      });
      setRecentRuns(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      showDialog(t('platformAdmin.workflowRuns.errorTitle'), (e as Error).message);
      setRecentRuns(null);
    } finally {
      setRecentLoading(false);
    }
  };

  const pickRun = (runId: string) => {
    setRunIdInput(runId);
    void inspectRun(runId);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <Text className="font-semibold text-lg">{t('platformAdmin.workflowRuns.title')}</Text>
      </CardHeader>
      <CardContent>
        <Button variant="outline" onPress={() => void loadRecentRuns()} disabled={recentLoading}>
          <Text className="font-medium">
            {recentLoading ? t('common.loading') : t('platformAdmin.workflowRuns.recentAction')}
          </Text>
        </Button>
        {recentRuns && (
          <View className="mt-2 mb-3" testID="workflow-runs-recent">
            {recentRuns.length === 0 ? (
              <Text className="text-sm text-gray-500">{t('platformAdmin.workflowRuns.recentEmpty')}</Text>
            ) : (
              recentRuns.map((run) => (
                <TouchableOpacity
                  key={run.id}
                  onPress={() => pickRun(run.id)}
                  accessibilityRole="button"
                  testID={`workflow-run-${run.id}`}
                  className="py-1"
                >
                  <Text className="text-xs text-gray-700" selectable>
                    {run.id}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {run.status} · {run.workflow_type} · {run.created_at}
                    {run.user_id ? ` · ${t('platformAdmin.workflowRuns.userLabel', { id: run.user_id })}` : ''}
                  </Text>
                </TouchableOpacity>
              ))
            )}
          </View>
        )}
        <TextInput
          value={runIdInput}
          onChangeText={setRunIdInput}
          placeholder={t('platformAdmin.workflowRuns.runIdPlaceholder')}
          accessibilityLabel={t('platformAdmin.workflowRuns.runIdPlaceholder')}
          className="border border-gray-300 rounded px-3 py-2 mb-2 mt-2"
          autoCapitalize="none"
          autoCorrect={false}
        />
        <Button onPress={() => void inspectRun(runIdInput)} disabled={runLoading}>
          <Text className="text-white font-medium">
            {runLoading ? t('common.loading') : t('platformAdmin.workflowRuns.inspectAction')}
          </Text>
        </Button>
        {runData && (
          <View className="mt-3">
            <Text className="font-medium">
              {t('platformAdmin.workflowRuns.statusLabel', { status: runData.status })}
            </Text>
            <Text className="text-sm text-gray-500">
              {t('platformAdmin.workflowRuns.typeLabel', { type: runData.workflow_type })}
            </Text>
            <Text className="text-sm font-medium mt-2">
              {t('platformAdmin.workflowRuns.nodesLabel', { count: runData.node_runs?.length ?? 0 })}
            </Text>
            {(runData.node_runs ?? []).map((nr: any) => (
              <Text key={nr.id} className="text-xs text-gray-600 ml-2">
                {nr.node_name}: {nr.status} {nr.duration_ms ? `(${nr.duration_ms.toFixed(0)}ms)` : ''}
              </Text>
            ))}
          </View>
        )}
      </CardContent>
    </Card>
  );
}
